import { TableColumn } from '@components/data-table/data-table.component';
import { TemplateControl, TemplateStep, TemplateBlock } from '../request-result-modal.types';
import { ControlValueTranslator } from '../utils/control-value-translator.util';
import { ControlValueService } from './control-value.service';

/**
 * Service class for building table columns and data
 */
export class TableBuilderService {
  constructor(
    private controlValueService: ControlValueService,
    private lang: string
  ) {}

  /**
   * Build table columns from controls
   */
  buildColumns(controls: TemplateControl[]): TableColumn[] {
    return controls.map(control => ({
      field: control.name,
      header: this.getControlHeader(control),
      type: control.type === 'checkbox' ? 'boolean' : 'text'
    }));
  }

  /**
   * Build table data row from controls
   */
  buildDataRow(controls: TemplateControl[]): any {
    const rowData: any = {};
    controls.forEach(control => {
      rowData[control.name] = this.controlValueService.getValueForTable(control, this.lang);
    });
    return rowData;
  }

  /**
   * Get table controls from step (excluding photos and polygons)
   */
  getStepTableControls(step: TemplateStep): TemplateControl[] {
    return step.controls.filter(control =>
      !this.isPhotoControlType(control.type) && control.type !== 'polygon'
    );
  }

  /**
   * Get table controls from block (excluding polygons)
   */
  getBlockTableControls(block: TemplateBlock): TemplateControl[] {
    return block.controls.filter(control => !this.isPhotoControlType(control.type) && control.type !== 'polygon');
  }

  /**
   * Build complete table configuration for a step
   */
  buildStepTable(step: TemplateStep): { columns: TableColumn[], data: any[] } {
    const controls = this.getStepTableControls(step);
    return {
      columns: this.buildColumns(controls),
      data: [this.buildDataRow(controls)]
    };
  }

  /**
   * Build complete table configuration for a block
   */
  buildBlockTable(block: TemplateBlock): { columns: TableColumn[], data: any[] } {
    const controls = this.getBlockTableControls(block);
    return {
      columns: this.buildColumns(controls),
      data: [this.buildDataRow(controls)]
    };
  }

  private isPhotoControlType(value: unknown): boolean {
    if (typeof value === 'number') {
      return value === 9;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'photo' || normalized === '9';
    }
    return false;
  }

  private getControlHeader(control: TemplateControl): string {
    const legacyLabel = ControlValueTranslator.getTranslatedLabel(control, this.lang);
    if (legacyLabel) {
      return legacyLabel;
    }

    const labels = (control as any)?.labels as Record<string, unknown> | undefined;
    if (labels && typeof labels === 'object') {
      const candidates = this.lang === 'ru'
        ? ['ru-RU', 'ru', 'en-US', 'en']
        : this.lang === 'uz'
          ? ['uz-Latn-UZ', 'uz', 'en-US', 'en']
          : ['en-US', 'en', 'ru-RU', 'ru'];

      for (const key of candidates) {
        const value = labels[key];
        if (typeof value === 'string' && value.trim() !== '') {
          return value;
        }
      }

      for (const value of Object.values(labels)) {
        if (typeof value === 'string' && value.trim() !== '') {
          return value;
        }
      }
    }

    return control.name || '-';
  }
}

